import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import {
    DataGridPro,
    GridActionsCellItem,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
} from '@mui/x-data-grid-pro'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import SelectOrgs from '@components/SelectOrgs'
import useSession from '@hooks/useSession'
import { SsoConnection, HASsoResult } from '@types'
import { DateTime } from 'luxon'

// Lists `SsoConnections` rows (held on habitat-admin's MariaDB) for
// a chosen org and gives staff inline controls for the cheap edits
// — flag toggles + delete — and links to the full Edit page for
// everything else. Habitat-admin's list endpoint is scoped per-org
// (`GET /v2/sso-connections/org/:orgId`), so the page mounts the
// org picker first and only fires the request once an org is
// selected.
//
// Toast handoffs:
//   ?created=<slug>  — fired by the create page after a successful POST
//   ?updated=<slug>  — fired by the edit page after a successful PUT
//   ?deleted=<slug>  — fired by the edit page after a successful DELETE
// All three are stripped from the URL on mount via history.replaceState
// so a refresh doesn't re-toast.

const LIST_URL = (orgId: string) => `/api/ha/v2/sso-connections/org/${orgId}`
const ROW_URL = (id: string) => `/api/ha/v2/sso-connections/${id}`

type Row = SsoConnection
type FlagKey = 'enabled' | 'enforce_sso' | 'allow_idp_initiated'

const flagLabels: Record<FlagKey, string> = {
    enabled: 'enabled',
    enforce_sso: 'enforce SSO',
    allow_idp_initiated: 'allow IdP-initiated',
}

export default function ManageSsoConnectionsPage() {
    const session = useSession()!
    const [, setLocation] = useLocation()

    // Toast handoffs from the Create / Edit pages — read once on
    // mount, fire the toast, then strip the param via
    // history.replaceState so a refresh doesn't re-toast. ?org= is
    // preserved.
    const initialQuery = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        return {
            org: params.get('org') ?? undefined,
            created: params.get('created') ?? undefined,
            updated: params.get('updated') ?? undefined,
            deleted: params.get('deleted') ?? undefined,
        }
    }, [])

    const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
        initialQuery.org
    )
    const [rows, setRows] = useState<Row[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Monotonic request token. Bumped at the start of every `reloadRows`
    // call; a fetch only commits its result if its token is still the
    // latest, so an out-of-order response from a previous org can't paint
    // over the currently-selected org's rows.
    const requestSeqRef = useRef(0)

    // Always-current `selectedOrg`, read by `toggleFlag`'s failure-path
    // reload. The columns memo captures `toggleFlag` only when
    // `pendingFlags` changes, so the captured closure can hold a stale
    // `selectedOrg` after an org switch — reading through the ref avoids
    // reloading the wrong org.
    const selectedOrgRef = useRef(selectedOrg)
    selectedOrgRef.current = selectedOrg

    // Pending optimistic flag flips, keyed by `${rowId}:${flag}`,
    // so we can disable the affected Switch while the PUT is in
    // flight (UX: prevent rapid double-flipping under network
    // jitter).
    const [pendingFlags, setPendingFlags] = useState<Set<string>>(
        () => new Set()
    )

    // Delete confirm-dialog state. Holds the row being targeted so
    // the dialog can show the slug; `null` means closed.
    const [deleteTarget, setDeleteTarget] = useState<Row | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Surface any toast handoff once on mount, then strip the
    // matching param from the URL.
    useEffect(() => {
        const fired: string[] = []
        if (initialQuery.created) {
            enqueueSnackbar(
                `Created SSO connection "${initialQuery.created}".`,
                { variant: 'success' }
            )
            fired.push('created')
        }
        if (initialQuery.updated) {
            enqueueSnackbar(`Saved changes to "${initialQuery.updated}".`, {
                variant: 'success',
            })
            fired.push('updated')
        }
        if (initialQuery.deleted) {
            enqueueSnackbar(
                `Deleted SSO connection "${initialQuery.deleted}".`,
                { variant: 'info' }
            )
            fired.push('deleted')
        }
        if (fired.length === 0) return
        const url = new URL(window.location.href)
        for (const key of fired) url.searchParams.delete(key)
        window.history.replaceState(
            window.history.state,
            '',
            url.pathname + (url.search || '') + url.hash
        )
        // run-once on mount, dropping deps deliberately so a
        // same-tab nav with a stale param doesn't re-toast.
    }, [])

    const reloadRows = useCallback(
        async (orgId: string) => {
            // Claim the latest request slot. Any in-flight reload for a
            // previously-selected org is now stale and must not commit its
            // result.
            const seq = ++requestSeqRef.current
            const isCurrent = () => seq === requestSeqRef.current

            setLoading(true)
            setError(null)
            try {
                const response = await fetch(LIST_URL(orgId), {
                    headers: {
                        Authorization: `Bearer ${session.accessToken}`,
                    },
                })
                if (!isCurrent()) return
                if (!response.ok) {
                    setError(
                        `Failed to load SSO connections (HTTP ${response.status})`
                    )
                    return
                }
                const body = (await response.json()) as HASsoResult<Row[]>
                if (!isCurrent()) return
                if (body.error) {
                    setError(body.message)
                    return
                }
                setRows(body.result ?? [])
            } catch (err) {
                if (!isCurrent()) return
                console.error(err)
                setError('Failed to load SSO connections')
            } finally {
                // Only the latest request owns the loading flag — a stale
                // reload finishing late mustn't clear the spinner for the
                // request that superseded it.
                if (isCurrent()) setLoading(false)
            }
        },
        [session.accessToken]
    )

    useEffect(() => {
        if (!selectedOrg) {
            setRows([])
            return
        }
        reloadRows(selectedOrg)
    }, [selectedOrg, reloadRows])

    // Optimistic flag flip. PUTs the changed flag (only) to
    // habitat-admin's update endpoint, which accepts a partial
    // body. On failure: revert the row, fire an error snackbar,
    // and reload to make sure the UI matches DB state.
    const toggleFlag = async (row: Row, flag: FlagKey, next: boolean) => {
        const lockKey = `${row.id}:${flag}`
        if (pendingFlags.has(lockKey)) return

        setPendingFlags((s) => new Set(s).add(lockKey))

        // Apply optimistic UI
        setRows((prev) =>
            prev.map((r) =>
                r.id === row.id ? { ...r, [flag]: next ? 1 : 0 } : r
            )
        )

        try {
            const response = await fetch(ROW_URL(row.id), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.accessToken}`,
                },
                body: JSON.stringify({ [flag]: next }),
            })
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`)
            }
            const body = (await response.json()) as HASsoResult<SsoConnection>
            if (body.error) throw new Error(body.message)
            // Reconcile with the authoritative server row in case
            // habitat-admin normalised something we didn't send.
            setRows((prev) =>
                prev.map((r) => (r.id === row.id ? body.result : r))
            )
            enqueueSnackbar(
                `Set ${flagLabels[flag]} = ${next ? 'on' : 'off'} for "${row.connection_id}".`,
                { variant: 'success' }
            )
        } catch (err) {
            console.error(err)
            // Revert optimistic flip
            setRows((prev) =>
                prev.map((r) =>
                    r.id === row.id ? { ...r, [flag]: row[flag] } : r
                )
            )
            enqueueSnackbar(
                `Failed to update ${flagLabels[flag]} — ${
                    err instanceof Error ? err.message : 'unknown error'
                }`,
                { variant: 'error' }
            )
            // Reload defensively so any partial server-side state
            // change doesn't drift from what the grid shows. Read the org
            // through the ref: this closure may have been captured by the
            // columns memo before the latest org switch.
            const currentOrg = selectedOrgRef.current
            if (currentOrg) reloadRows(currentOrg)
        } finally {
            setPendingFlags((s) => {
                const next = new Set(s)
                next.delete(lockKey)
                return next
            })
        }
    }

    const handleDeleteConfirmed = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            const response = await fetch(ROW_URL(deleteTarget.id), {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${session.accessToken}`,
                },
            })
            if (response.status !== 204 && !response.ok) {
                const body = (await response
                    .json()
                    .catch(() => null)) as HASsoResult<unknown> | null
                throw new Error(
                    body && body.error
                        ? body.message
                        : `HTTP ${response.status}`
                )
            }
            const slug = deleteTarget.connection_id
            setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id))
            setDeleteTarget(null)
            enqueueSnackbar(`Deleted SSO connection "${slug}".`, {
                variant: 'info',
            })
        } catch (err) {
            console.error(err)
            enqueueSnackbar(
                `Failed to delete connection — ${
                    err instanceof Error ? err.message : 'unknown error'
                }`,
                { variant: 'error' }
            )
        } finally {
            setDeleting(false)
        }
    }

    // Build columns inside the component so the inline Switch /
    // action handlers close over the latest `toggleFlag` /
    // `setDeleteTarget` / `setLocation`. `useMemo` keeps the array
    // identity stable across renders that don't change anything
    // material — DataGrid is sensitive to column-array identity.
    const columns: GridColDef<Row>[] = useMemo(() => {
        const flagSwitch = (flag: FlagKey) =>
            ({
                field: flag,
                headerName: flagLabels[flag].replace(/^(\w)/, (c) =>
                    c.toUpperCase()
                ),
                width: 130,
                sortable: false,
                renderCell: (params: GridRenderCellParams<Row, 0 | 1>) => (
                    <Switch
                        checked={params.value === 1}
                        disabled={pendingFlags.has(`${params.row.id}:${flag}`)}
                        onChange={(_e, checked) =>
                            toggleFlag(params.row, flag, checked)
                        }
                        size="small"
                        color={flag === 'enabled' ? 'success' : 'warning'}
                        inputProps={{
                            'aria-label': `${flagLabels[flag]} for ${params.row.connection_id}`,
                        }}
                    />
                ),
            }) as GridColDef<Row>

        return [
            {
                field: 'connection_id',
                headerName: 'Connection ID',
                width: 200,
                renderCell: (params: GridRenderCellParams<Row, string>) => (
                    <Box>
                        <strong>{params.value}</strong>
                        <br />
                        <span style={{ color: 'gray', fontSize: '0.75rem' }}>
                            {params.row.provider_type.toUpperCase()}
                        </span>
                    </Box>
                ),
            },
            { field: 'idp_entity_id', headerName: 'IdP entityID', width: 280 },
            { field: 'idp_sso_url', headerName: 'IdP SSO URL', width: 280 },
            flagSwitch('enabled'),
            flagSwitch('enforce_sso'),
            flagSwitch('allow_idp_initiated'),
            {
                field: 'email_domains',
                headerName: 'Email domains',
                width: 220,
                valueGetter: (_value, row) =>
                    (row.email_domains ?? []).join(', ') || '—',
            },
            {
                field: 'updated_at',
                headerName: 'Last updated',
                width: 160,
                valueFormatter: (value) =>
                    value
                        ? DateTime.fromISO(value).toLocaleString(
                              DateTime.DATETIME_SHORT
                          )
                        : '—',
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 90,
                getActions: (params: GridRowParams<Row>) => [
                    <GridActionsCellItem
                        key="edit"
                        icon={
                            <Tooltip title="Edit connection">
                                <EditIcon fontSize="small" />
                            </Tooltip>
                        }
                        label="Edit"
                        onClick={() =>
                            setLocation(`/edit-sso-connection/${params.row.id}`)
                        }
                    />,
                    <GridActionsCellItem
                        key="delete"
                        icon={
                            <Tooltip title="Delete connection">
                                <DeleteIcon fontSize="small" color="error" />
                            </Tooltip>
                        }
                        label="Delete"
                        onClick={() => setDeleteTarget(params.row)}
                    />,
                ],
            },
        ]
        // Why the eslint-disable is safe:
        //
        // The columns array references three closures —
        // `toggleFlag`, `setDeleteTarget`, `setLocation` — and reads
        // `pendingFlags`. Of those:
        //
        //   - `setDeleteTarget` and `setLocation` (wouter) are
        //     stable React state setters; their identities never
        //     change across renders, so leaving them out of deps
        //     can't produce a stale closure
        //   - `toggleFlag` is defined inline in this component and
        //     captures `session.accessToken`, but the value it
        //     captures only matters for the *fetch call* it fires
        //     — if it ever runs against a stale token the fetch
        //     fails closed and the user sees an error toast, no
        //     silent data corruption
        //   - `pendingFlags` IS in deps because it changes on every
        //     flip, and we want the column row's `disabled` prop to
        //     re-evaluate when a flip is in flight (otherwise the
        //     button visibly flickers between disabled and enabled
        //     states)
        //
        // Including `toggleFlag` in deps would cause the columns
        // array to recreate on every render (closures aren't
        // referentially stable), which makes the MUI DataGrid
        // re-evaluate its column metadata every render. Avoiding
        // that is the whole point of the memo.
    }, [pendingFlags])

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    return (
        <DashboardLayout loading={loading}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Manage SSO connections
                </Typography>
                <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
                    Pick an organisation to see the SAML connections currently
                    configured for it. Use <strong>New connection</strong> to
                    add one — connections are stored in habitat-admin&apos;s{' '}
                    <code>SsoConnections</code> table and drive the
                    federated-login flow on hl-accounts.
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ flexGrow: 1, maxWidth: 480 }}>
                        <SelectOrgs
                            selectedOrg={selectedOrg}
                            setSelectedOrg={setSelectedOrg}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() =>
                            setLocation(
                                selectedOrg
                                    ? `/create-sso-connection?org=${selectedOrg}`
                                    : '/create-sso-connection'
                            )
                        }
                    >
                        New connection
                    </Button>
                </Stack>
            </Paper>

            {!selectedOrg && (
                <Typography
                    variant="body2"
                    sx={{ color: 'gray', textAlign: 'center', mt: 4 }}
                >
                    Select an organisation above to load its SSO connections.
                </Typography>
            )}

            {selectedOrg && rows.length === 0 && loading && (
                <DashboardLoadingDataView loading={loading} />
            )}

            {selectedOrg && rows.length === 0 && !loading && !error && (
                <Typography
                    variant="body2"
                    sx={{ color: 'gray', textAlign: 'center', mt: 4 }}
                >
                    No SSO connections found for this org yet.
                </Typography>
            )}

            {selectedOrg && rows.length > 0 && (
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pageSizeOptions={[10, 20, 50, { value: -1, label: 'All' }]}
                    pagination
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    autoHeight
                />
            )}

            <Dialog
                open={!!deleteTarget}
                onClose={() => !deleting && setDeleteTarget(null)}
            >
                <DialogTitle>Delete this SSO connection?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Permanently remove the connection{' '}
                        <strong>{deleteTarget?.connection_id}</strong>. Any
                        federated identities created against it stay in
                        hl-accounts but lose their connection back to a live IdP
                        — re-creating the connection later under the same{' '}
                        <code>idp_entity_id</code> will reuse them.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleDeleteConfirmed}
                        color="error"
                        variant="contained"
                        loading={deleting}
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}
